import { createAsyncThunk, AnyAction } from "@reduxjs/toolkit"
import { ThunkDispatch } from "redux-thunk"

import i18n from "config/i18n"
import { API } from "redux/api"
import {
  ID_CARD_SIGN_SESSION_STATUS_DONE,
  POLLING_INTERVAL,
  POLLING_TIMEOUT,
} from "config"
import PollingService from "redux/api/pollingService"
import {
  start as startPolling,
  reset as resetPolling,
} from "redux/features/polling/pollingSlice"
import { PrepareIdCardSignValues, PrepareIdCardSignResponseType } from "types"

export const createIdCardSignSession = createAsyncThunk(
  "id-card-sign-session/create",
  async (values: PrepareIdCardSignValues) => {
    const response = await API.post("/id_card_sign/prepare", values)
    return response.data
  },
)

export const fetchIdCardSignSession = createAsyncThunk(
  "id-card-sign-session/fetch",
  async (values: { session_id: string }) => {
    const response = await API.get(`/id_card_sign/${values.session_id}`)

    return response.data
  },
)

export const makePrepareIdCardSign = (values: PrepareIdCardSignValues) => {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    return new Promise<PrepareIdCardSignResponseType>((resolve, reject) => {
      return dispatch(createIdCardSignSession(values))
        .then(
          ({ payload }) => {
            if (!payload || !payload.id_card_sign) {
              return reject(i18n.t("notice.id_card.session.unavailable"))
            }

            const { session_id } = payload.id_card_sign

            let promise_result: string

            const timerId = setInterval(async () => {
              const pollingId = PollingService.getPolling()

              if (!pollingId) {
                clearInterval(timerId)
                return reject(i18n.t("common.request_cancelled"))
              }

              const result = await dispatch(
                fetchIdCardSignSession({
                  session_id,
                }),
              )

              if (result?.payload?.id_card_sign) {
                const { status, action_payload } = result.payload.id_card_sign

                // NOTE: clear interval when status = success
                if (status === ID_CARD_SIGN_SESSION_STATUS_DONE) {
                  clearInterval(timerId)
                  dispatch(resetPolling())

                  const { digest_value, hash_function } = action_payload

                  return resolve({
                    digest_value,
                    hash_function,
                  })
                }
              }
            }, POLLING_INTERVAL)

            dispatch(startPolling({ pollingId: timerId }))

            setTimeout(() => {
              clearInterval(timerId)
              dispatch(resetPolling())
              if (!promise_result) {
                reject(i18n.t("notice.id_card.sign.failure"))
              }
            }, POLLING_TIMEOUT)
          },
          () => {
            reject(i18n.t("notice.id_card.session.failure"))
          },
        )
        .catch((error) => {
          reject(error)
        })
    })
  }
}
